import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Layers, Search, Loader2, KeyRound, Ban, RotateCcw, ShieldCheck, LogOut, RefreshCw,
  DollarSign, CheckCircle2, TrendingUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  active: "text-[#D4FF00] border-[#D4FF00]/40",
  unclaimed: "text-[#A3A3A3] border-[#333]",
  deactivated: "text-[#FF9F43] border-[#FF9F43]/40",
  refunded: "text-[#FF3B30] border-[#FF3B30]/40",
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("pf_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [busyKey, setBusyKey] = useState("");

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [lic, st] = await Promise.all([
        axios.get(`${API}/admin/licenses`, { headers: authHeader(), params: query ? { query } : {} }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      ]);
      setRows(lic.data.licenses);
      setStats(st.data);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthed(false);
        setError("Invalid admin token.");
        localStorage.removeItem("pf_admin_token");
        setToken("");
      } else {
        setError("Failed to load licenses.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, query]);

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, {}, { headers: { Authorization: `Bearer ${t}` } });
      localStorage.setItem("pf_admin_token", t);
      setToken(t);
      setAuthed(true);
    } catch (e) {
      setError("Invalid admin token.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authed]);

  const signOut = () => {
    localStorage.removeItem("pf_admin_token");
    setToken("");
    setAuthed(false);
    setRows([]);
  };

  const act = async (key, kind) => {
    if (kind === "refund" && !window.confirm(`Refund and revoke ${key}? This issues a Stripe refund.`)) return;
    if (kind === "deactivate" && !window.confirm(`Deactivate ${key}? Exports will be blocked.`)) return;
    setBusyKey(key);
    try {
      await axios.post(`${API}/admin/licenses/${key}/${kind}`, {}, { headers: authHeader() });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || `${kind} failed.`);
    } finally {
      setBusyKey("");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white noise-overlay flex items-center justify-center px-5">
        <form onSubmit={signIn} className="w-full max-w-sm border border-[#262626] bg-[#0f0f0f] p-8" data-testid="admin-login">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-6 h-6 bg-[#D4FF00] flex items-center justify-center">
              <ShieldCheck className="w-3.5 h-3.5 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-head font-extrabold tracking-tighter">Admin · PrintFade</span>
          </div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">Admin Token</label>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            data-testid="admin-token-input"
            placeholder="Enter admin token"
            className="mt-2 w-full bg-[#141414] border border-[#262626] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-[#D4FF00]"
          />
          {error && <p className="mt-3 font-mono text-[11px] text-[#FF3B30]">{error}</p>}
          <button
            type="submit"
            disabled={loading || !tokenInput.trim()}
            data-testid="admin-login-btn"
            className="mt-5 w-full bg-[#D4FF00] text-black font-bold py-3 hover:bg-white transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" strokeWidth={2.5} />}
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-overlay">
      <header className="h-14 border-b border-[#262626] flex items-center justify-between px-5">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-[#D4FF00] flex items-center justify-center">
            <Layers className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-head font-extrabold tracking-tighter">PrintFade <span className="text-[#D4FF00]">Admin</span></span>
        </div>
        <button onClick={signOut} data-testid="admin-logout-btn" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#A3A3A3] hover:text-white transition-colors">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {/* Sales Insights */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="admin-stats">
          {[
            { label: "Total Sales", value: stats ? `$${((stats.total_sales_amount || 0) / 100).toFixed(2)}` : "—", sub: stats ? `${stats.total_sales_count} order(s)` : "", icon: DollarSign, accent: true },
            { label: "Active Licenses", value: stats ? stats.active_licenses : "—", sub: stats ? `${stats.total_licenses} total` : "", icon: CheckCircle2 },
            { label: "Refunds", value: stats ? `$${((stats.refunded_amount || 0) / 100).toFixed(2)}` : "—", sub: stats ? `${stats.refunded_count} refund(s)` : "", icon: RotateCcw, danger: true },
            { label: "Net Revenue", value: stats ? `$${((stats.net_revenue_amount || 0) / 100).toFixed(2)}` : "—", sub: "after refunds", icon: TrendingUp },
          ].map((c) => (
            <div key={c.label} className="border border-[#262626] bg-[#0f0f0f] p-4" data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{c.label}</span>
                <c.icon className={`w-4 h-4 ${c.danger ? "text-[#FF3B30]" : c.accent ? "text-[#D4FF00]" : "text-[#666]"}`} strokeWidth={2.5} />
              </div>
              <div className={`font-head font-extrabold text-2xl tracking-tighter ${c.accent ? "text-[#D4FF00]" : "text-white"}`}>{c.value}</div>
              <div className="font-mono text-[10px] text-[#666] mt-1">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-[#666] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="admin-search-input"
              placeholder="Search by license key or session id…"
              className="w-full bg-[#141414] border border-[#262626] pl-9 pr-3 py-2.5 font-mono text-sm outline-none focus:border-[#D4FF00]"
            />
          </div>
          <button onClick={load} data-testid="admin-refresh-btn" className="border border-[#333] px-4 py-2.5 hover:bg-white hover:text-black transition-colors flex items-center gap-2 font-semibold text-sm">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {error && <p className="mb-4 font-mono text-[11px] text-[#FF3B30]" data-testid="admin-error">{error}</p>}

        <div className="border border-[#262626] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-license-table">
            <thead>
              <tr className="bg-[#141414] font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                <th className="text-left px-4 py-3">License Key</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Device</th>
                <th className="text-left px-4 py-3">Transfers</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#666]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center font-mono text-xs text-[#666]">No licenses found.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.license_key} className="border-t border-[#1c1c1c] hover:bg-white/[0.02]" data-testid={`admin-row-${r.license_key}`}>
                  <td className="px-4 py-3 font-mono text-[#D4FF00]">{r.license_key}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${STATUS_COLORS[r.status] || "text-[#A3A3A3] border-[#333]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#A3A3A3]">
                    {r.device_bound ? `${String(r.device_id).slice(0, 10)}…` : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#A3A3A3]">{r.transfer_count}</td>
                  <td className="px-4 py-3 font-mono text-[11px]">{r.amount != null ? `$${(r.amount / 100).toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#A3A3A3]">{r.payment_status || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => act(r.license_key, "deactivate")}
                        disabled={busyKey === r.license_key || r.status === "refunded" || r.status === "deactivated"}
                        data-testid={`deactivate-${r.license_key}`}
                        className="flex items-center gap-1 border border-[#333] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FF9F43] hover:text-black hover:border-[#FF9F43] transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white"
                      >
                        <Ban className="w-3 h-3" /> Deactivate
                      </button>
                      <button
                        onClick={() => act(r.license_key, "refund")}
                        disabled={busyKey === r.license_key || r.status === "refunded" || r.payment_status !== "paid"}
                        data-testid={`refund-${r.license_key}`}
                        className="flex items-center gap-1 border border-[#333] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FF3B30] hover:text-white hover:border-[#FF3B30] transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        {busyKey === r.license_key ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Refund
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
